import { useNavigate } from "react-router-dom";
import BoothMark from "./BoothMark";
import "./SampleDispatch.css";

const LEAGUE_NAME = "The League";
const DATELINE = "Week 2, Tuesday, October 20";

const matchups = [
  { winner: { name: "Purdy In Pink", score: "112.08" }, loser: { name: "To Cook a Mockingbird", score: "110.92" } },
  { winner: { name: "Allen Wrench", score: "109.22" }, loser: { name: "Kelce The Lights On", score: "94.48" } },
  { winner: { name: "Dak To The Future", score: "92.26" }, loser: { name: "Stroud and Clear", score: "39.56" } },
  { winner: { name: "St. Brown Sugar", score: "119.46" }, loser: { name: "Kittle Did I Know", score: "85.44" } },
  { winner: { name: "Smith-Njigba Can't Win", score: "129.82" }, loser: { name: "McCaffrey or Bust", score: "101.62" } },
  { winner: { name: "Taylor Made", score: "79.50" }, loser: { name: "CeeDee Vu", score: "69.70" } },
];

const lead = {
  headline: "A quarterback on the bench cost To Cook a Mockingbird the week",
  dek: "Patrick Mahomes scored 29.0 on the bench. Caleb Williams started and scored 7.7.",
  winner: { name: "Purdy In Pink", score: "112.08" },
  loser: { name: "To Cook a Mockingbird", score: "110.92" },
  margin: "1.16",
  why: "A bench decision cost 21.3.",
  paragraphs: [
    "To Cook a Mockingbird fell to Purdy In Pink by 1.16 points, and the postgame film shows exactly where it went. Caleb Williams started and scored 7.7. Patrick Mahomes sat on the bench and scored 29.0. That one decision swung the matchup by 21.3 points, the difference between a narrow loss and a comfortable win.",
    "Brock Purdy gave the winners their floor: 287 passing yards, two touchdowns and a rushing score for 28.5 points. Ja'Marr Chase added two more scores. In a week decided by a single field goal's worth of decimals, the quarterback left on the bench is the one To Cook a Mockingbird will be thinking about until Thursday.",
  ],
  performers: [
    {
      team: "Purdy In Pink",
      players: [
        { pos: "QB", name: "Brock Purdy", stats: "287 pass yds, 2 TD, 30 rush yds, 1 rush TD", pts: "28.5" },
        { pos: "WR", name: "Ja'Marr Chase", stats: "7 rec, 75 yds, 2 TD", pts: "23.0" },
        { pos: "RB", name: "Jahmyr Gibbs", stats: "52 rush yds, 6 rec, 61 rec yds, 1 rec TD", pts: "20.3" },
      ],
    },
    {
      team: "To Cook a Mockingbird",
      players: [
        { pos: "RB", name: "James Cook III", stats: "135 rush yds, 1 rush TD, 1 rec", pts: "20.4" },
        { pos: "WR", name: "Chris Olave", stats: "8 rec, 86 yds, 1 TD", pts: "18.6" },
        { pos: "BN", name: "Patrick Mahomes", stats: "On the bench. 3 pass TD, 1 rush TD", pts: "29.0", bench: true },
      ],
    },
  ],
};

const moreStories = [
  { headline: "Allen's heroics carry Allen Wrench past Kelce The Lights On", margin: "14.74" },
  { headline: "Prescott throws four as Dak To The Future run the streak to six", margin: "52.70" },
  { headline: "Amon-Ra St. Brown turns St. Brown Sugar loose on Kittle Did I Know", margin: "34.02" },
  { headline: "Jaxon Smith-Njigba scores 40, and the ironic name finally fails", margin: "28.20" },
  { headline: "Jonathan Taylor ends Taylor Made's four-game skid", margin: "9.80" },
];

/** One section of the dispatch preview paired with a caption explaining the real feature it stands in for. */
function AnnotatedRow({ side, label, body, children }) {
  return (
    <div className={`booth-sample-row booth-sample-row--${side}`}>
      <div className="booth-sample-content">{children}</div>
      <div className="booth-sample-annotation">
        <span className="booth-sample-annotation-rule" aria-hidden="true" />
        <span className="booth-sample-annotation-label">{label}</span>
        <span className="booth-sample-annotation-body">{body}</span>
      </div>
    </div>
  );
}

function SampleDispatch() {
  const navigate = useNavigate();
  const goToAuth = () => navigate("/auth");

  return (
    <section id="sample" aria-label="A sample dispatch" className="booth-shell booth-sample">
      <div className="booth-sample-intro">
        <p className="booth-sample-eyebrow">What lands every Tuesday morning</p>
        <h2 className="booth-section-title">This is a dispatch.</h2>
        <p className="booth-section-dek">
          One story for every matchup in your league. Written overnight, checked against the box score, filed
          before your coffee.
        </p>
      </div>

      <div className="booth-sample-paper">
        <AnnotatedRow
          side="right"
          label="Your league on the front page"
          body="A dispatch with your league's name on it, in every manager's inbox Tuesday at 5:00 AM."
        >
          <div className="booth-sample-masthead">
            <BoothMark className="booth-sample-wordmark" />
            <div className="booth-sample-dateline">
              <span className="booth-sample-league">{LEAGUE_NAME}</span>
              <span className="booth-sample-date">{DATELINE}</span>
            </div>
          </div>
        </AnnotatedRow>

        <AnnotatedRow side="left" label="The week at a glance" body="Every score in one place, winner first.">
          <table className="booth-sample-scoreboard" aria-label="Week 2 scores, winner first">
            <tbody>
              {matchups.map((m) => (
                <tr key={m.winner.name}>
                  <td className="tname booth-sample-sb-name booth-sample-sb-name--winner">{m.winner.name}</td>
                  <td className="booth-sample-sb-score booth-sample-sb-score--winner">{m.winner.score}</td>
                  <td className="booth-sample-sb-score booth-sample-sb-score--loser">{m.loser.score}</td>
                  <td className="tname booth-sample-sb-name booth-sample-sb-name--loser">{m.loser.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AnnotatedRow>

        <AnnotatedRow
          side="right"
          label="The lead"
          body="The desk picks the week's best story. Usually the closest game, sometimes the strangest."
        >
          <div className="booth-sample-lead">
            <span className="booth-sample-lead-kicker">The lead</span>
            <h3 className="booth-sample-lead-headline">{lead.headline}</h3>
            <p className="booth-sample-lead-dek">{lead.dek}</p>
          </div>
        </AnnotatedRow>

        <AnnotatedRow
          side="left"
          label="Stated once, plainly"
          body="The final, the margin and the decision that swung it."
        >
          <div className="booth-sample-boxscore">
            <div className="booth-sample-boxscore-row booth-sample-boxscore-row--winner">
              <span className="tname">{lead.winner.name}</span>
              <span>{lead.winner.score}</span>
            </div>
            <div className="booth-sample-boxscore-row booth-sample-boxscore-row--loser">
              <span className="tname">{lead.loser.name}</span>
              <span>{lead.loser.score}</span>
            </div>
            <p className="booth-sample-boxscore-result">
              <span className="booth-sample-boxscore-margin">Won by {lead.margin}.</span> {lead.why}
            </p>
          </div>
        </AnnotatedRow>

        <AnnotatedRow
          side="right"
          label="Reported, then checked"
          body="Written like a beat reporter, checked like an editor. Every number comes from your league's box score."
        >
          <div className="booth-sample-story">
            {lead.paragraphs.map((graf) => (
              <p key={graf.slice(0, 24)}>{graf}</p>
            ))}
          </div>
        </AnnotatedRow>

        <AnnotatedRow side="left" label="The receipts" body="Who carried whom, including the points left on the bench.">
          <div className="booth-sample-performers">
            <h4 className="booth-sample-performers-title">Top performers</h4>
            {lead.performers.map((group) => (
              <div key={group.team}>
                <div className="tname booth-sample-performers-team">{group.team}</div>
                {group.players.map((p) => (
                  <div
                    key={p.name}
                    className={`booth-sample-performer ${p.bench ? "booth-sample-performer--bench" : ""}`.trim()}
                  >
                    <span className="booth-sample-performer-pos">{p.pos}</span>
                    <span className="booth-sample-performer-info">
                      <span className="booth-sample-performer-name">{p.name}</span>
                      <span className="booth-sample-performer-stats">{p.stats}</span>
                    </span>
                    <span className="booth-sample-performer-pts">{p.pts}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </AnnotatedRow>

        <AnnotatedRow
          side="right"
          label="Every matchup gets one"
          body="Six games, six stories. Nobody's week goes unreported."
        >
          <div className="booth-sample-more">
            <h4 className="booth-sample-more-title">Also in this edition</h4>
            {moreStories.map((s) => (
              <button key={s.headline} type="button" className="booth-sample-more-row" onClick={goToAuth}>
                <span className="booth-sample-more-headline">{s.headline}</span>
                <span className="booth-sample-more-margin">Won by {s.margin}</span>
              </button>
            ))}
          </div>
        </AnnotatedRow>
      </div>

      <div className="booth-sample-cta">
        <button type="button" className="booth-btn booth-btn--primary" onClick={goToAuth}>
          Get one for your league <span aria-hidden="true">→</span>
        </button>
        <span className="booth-sample-footnote">A sample dispatch from week 2 of a real league.</span>
      </div>
    </section>
  );
}

export default SampleDispatch;
